import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContractService } from 'src/app/services/contract.service';
import { ToastService } from 'src/app/services/toast.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-related-persons-edit-modal',
  templateUrl: './related-persons-edit-modal.component.html',
  styleUrls: ['./related-persons-edit-modal.component.css']
})
export class RelatedPersonsEditModalComponent implements OnInit, OnDestroy {
  @Input() type: 'owners' | 'tenants' | 'guarantors';
  @Input() contract: any;
  @Input() contractId: number;

  @ViewChild('OwnerForm') ownerForm: ElementRef;
  @ViewChild('TenantForm') tenantForm: ElementRef;
  @ViewChild('GuarantorForm') guarantorForm: ElementRef;
  @ViewChild('SearchModal') searchModal: ElementRef;

  selectedPersons: any[] = [];
  loading = false;
  private destroy$ = new Subject<void>();
  
  // Referencias a los modales abiertos
  private createModalRef: any;
  private searchModalRef: any;

  constructor(
    private contractService: ContractService,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.initializeSelectedPersons();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSelectedPersons() {
    if (this.contract) {
      switch (this.type) {
        case 'owners':
          this.selectedPersons = [...(this.contract.owners || [])];
          break;
        case 'tenants':
          this.selectedPersons = [...(this.contract.tenants || [])];
          break;
        case 'guarantors':
          this.selectedPersons = [...(this.contract.guarantors || [])];
          break;
      }
    }
  }

  onEntitySelected(entity: any) {
    this.onPersonSelected(entity);
  }

  onEntityCreated(entity: any) {
    this.onPersonCreated(entity);
  }


  private isPersonAlreadySelected(entity: any): boolean {
    console.log('Checking if person is already selected:', entity);
    const result = this.selectedPersons.some(p => {
      // Validar por ID si existe
      if (p.id && entity.id && p.id === entity.id) {
        console.log('Match found by ID:', p.id, '===', entity.id);
        return true;
      }
      
      // Validar por DNI si existe
      const pDni = this.getPersonDocument(p);
      const entityDni = this.getPersonDocument(entity);
      console.log('Comparing DNI:', pDni, '===', entityDni);
      if (pDni && entityDni && pDni === entityDni) {
        console.log('Match found by DNI:', pDni, '===', entityDni);
        return true;
      }
      
      return false;
    });
    console.log('isPersonAlreadySelected result:', result);
    return result;
  }

  getTitle(): string {
    switch (this.type) {
      case 'owners':
        return 'Editar Propietarios';
      case 'tenants':
        return 'Editar Inquilinos';
      case 'guarantors':
        return 'Editar Garantes';
      default:
        return '';
    }
  }

  getSearchType(): string {
    const searchType = (() => {
      switch (this.type) {
        case 'owners':
          return 'owner';
        case 'tenants':
          return 'tenant';
        case 'guarantors':
          return 'guarantor';
        default:
          return '';
      }
    })();
    console.log('RelatedPersonsEditModal - getSearchType() called with type:', this.type, 'returning:', searchType);
    return searchType;
  }

  getPersonTypeLabel(): string {
    switch (this.type) {
      case 'owners':
        return 'Propietario';
      case 'tenants':
        return 'Inquilino';
      case 'guarantors':
        return 'Garante';
      default:
        return '';
    }
  }

  onDelete(index: number) {
    if (index >= 0 && index < this.selectedPersons.length) {
      const person = this.selectedPersons[index];
      const personName = this.getPersonName(person);
      this.selectedPersons.splice(index, 1);
      this.toastService.showToast('info', `${personName} eliminado de la selección`);
    }
  }

  clearAllSelections() {
    if (this.selectedPersons.length > 0) {
      this.selectedPersons = [];
      this.toastService.showToast('info', 'Todas las selecciones han sido eliminadas');
    }
  }

  save() {
    if (this.selectedPersons.length === 0) {
      this.toastService.showToast('warning', 'Debe seleccionar al menos una persona');
      return;
    }

    this.loading = true;
    
    console.log('Saving related persons:', {
      contractId: this.contractId,
      type: this.type,
      persons: this.selectedPersons,
      personsCount: this.selectedPersons.length
    });
    
    this.contractService.updateRelatedPersons({
      contractId: this.contractId,
      type: this.type,
      persons: this.selectedPersons
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response) => {
        console.log('Update successful:', response);
        this.toastService.showToast('success', 'Personas relacionadas actualizadas correctamente');
        this.activeModal.close(true);
      },
      error: (error) => {
        console.error('Error updating related persons:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          url: error.url
        });
        
        let errorMessage = 'Error desconocido';
        if (error.status === 400) {
          errorMessage = error.error?.message || error.error || 'Datos inválidos enviados al servidor';
        } else if (error.status === 500) {
          errorMessage = 'Error interno del servidor';
        } else if (error.status === 0) {
          errorMessage = 'No se pudo conectar al servidor';
        }
        
        this.toastService.showToast('error', `Error al actualizar las personas relacionadas: ${errorMessage}`);
        this.loading = false;
      }
    });
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  trackByPersonId(index: number, person: any): any {
    return person.id || index;
  }

  getPersonName(person: any): string {
    console.log('Getting person name for:', person);
    console.log('Type:', this.type);
    
    // Intentar múltiples estructuras de datos
    let name = '';
    
    // Para garantes
    if (this.type === 'guarantors') {
      // Estructura 1: person.person.businessName
      if (person.person?.businessName) {
        name = person.person.businessName;
      }
      // Estructura 2: person.person.firstName + lastName
      else if (person.person?.firstName && person.person?.lastName) {
        name = `${person.person.firstName} ${person.person.lastName}`;
      }
      // Estructura 3: person.person.fullName
      else if (person.person?.fullName) {
        name = person.person.fullName;
      }
      // Estructura 4: campos directos
      else if (person.businessName) {
        name = person.businessName;
      }
      else if (person.firstName && person.lastName) {
        name = `${person.firstName} ${person.lastName}`;
      }
      else if (person.fullName) {
        name = person.fullName;
      }
      else if (person.name) {
        name = person.name;
      }
    } 
    // Para propietarios e inquilinos
    else {
      // Estructura 1: person.user.person.fullName
      if (person.user?.person?.fullName) {
        name = person.user.person.fullName;
      }
      // Estructura 2: person.user.person.firstName + lastName
      else if (person.user?.person?.firstName && person.user?.person?.lastName) {
        name = `${person.user.person.firstName} ${person.user.person.lastName}`;
      }
      // Estructura 3: person.user.person.businessName
      else if (person.user?.person?.businessName) {
        name = person.user.person.businessName;
      }
      // Estructura 4: campos directos en person
      else if (person.fullName) {
        name = person.fullName;
      }
      else if (person.firstName && person.lastName) {
        name = `${person.firstName} ${person.lastName}`;
      }
      else if (person.businessName) {
        name = person.businessName;
      }
      else if (person.name) {
        name = person.name;
      }
    }
    
    console.log('Extracted name:', name);
    return name || 'Sin nombre';
  }

  getPersonInitials(person: any): string {
    const name = this.getPersonName(person);
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getPersonDocument(person: any): string {
    console.log('Getting person document for:', person);
    
    let document = '';
    
    if (this.type === 'guarantors') {
      // Múltiples estructuras para garantes
      document = person.person?.documentId || 
                 person.documentId || 
                 person.person?.document || 
                 person.document || 
                 person.person?.dni || 
                 person.dni || 
                 '';
    } else {
      // Múltiples estructuras para propietarios e inquilinos
      document = person.user?.person?.documentId || 
                 person.documentId || 
                 person.user?.person?.document || 
                 person.document || 
                 person.user?.person?.dni || 
                 person.dni || 
                 '';
    }
    
    console.log('Extracted document:', document);
    return document || 'Sin documento';
  }

  getPersonEmail(person: any): string {
    console.log('Getting person email for:', person);
    
    let email = '';
    
    if (this.type === 'guarantors') {
      email = person.email || 
              person.user?.email || 
              person.person?.email || 
              '';
    } else {
      email = person.user?.email || 
              person.email || 
              person.user?.person?.email || 
              '';
    }
    
    console.log('Extracted email:', email);
    return email || 'Sin email';
  }

  getPersonAddress(person: any): string {
    console.log('Getting person address for:', person);
    let address = '';
    
    // Para personas creadas (vienen de los formularios) - buscar directamente en el objeto
    if (person.streetAddress || person.city || person.state || person.zipCode) {
      const parts = [
        person.streetAddress || '',
        person.city || '',
        person.state || '',
        person.zipCode || ''
      ].filter(part => part.trim() !== '');
      address = parts.join(', ');
    }
    // Para personas existentes (vienen de la base de datos) - buscar en estructuras anidadas
    else if (this.type === 'guarantors') {
      if (person.person?.address) {
        const addr = person.person.address;
        address = `${addr.streetAddress || ''} ${addr.city || ''} ${addr.state || ''}`.trim();
      } else if (person.address) {
        address = person.address;
      }
    } else {
      if (person.user?.person?.address) {
        const addr = person.user.person.address;
        address = `${addr.streetAddress || ''} ${addr.city || ''} ${addr.state || ''}`.trim();
      } else if (person.address) {
        address = person.address;
      }
    }
    
    console.log('Extracted address:', address);
    return address || 'Sin dirección';
  }

  // Métodos para manejar los modales directos
  openCreateModal() {
    console.log('Opening create modal for type:', this.type);
    let formTemplate: ElementRef;
    
    switch (this.type) {
      case 'owners':
        formTemplate = this.ownerForm;
        break;
      case 'tenants':
        formTemplate = this.tenantForm;
        break;
      case 'guarantors':
        formTemplate = this.guarantorForm;
        break;
      default:
        return;
    }
    
    if (formTemplate) {
      this.createModalRef = this.modalService.open(formTemplate, { size: 'lg' });
    }
  }

  openSearchModal() {
    console.log('Opening search modal for type:', this.type);
    if (this.searchModal) {
      this.searchModalRef = this.modalService.open(this.searchModal, { size: 'lg' });
    }
  }

  onPersonCreated(person: any) {
    console.log('Person created - Full object:', person);
    console.log('Person created - Type:', this.type);
    console.log('Person created - Structure analysis:', {
      hasUser: !!person.user,
      hasPerson: !!person.person,
      hasName: !!person.name,
      hasFullName: !!person.fullName,
      hasEmail: !!person.email,
      hasDocumentId: !!person.documentId,
      userPerson: person.user?.person,
      directPerson: person.person
    });
    
    if (person && !this.isPersonAlreadySelected(person)) {
      this.selectedPersons = [...this.selectedPersons, person];
      this.toastService.showToast('success', 'Persona creada y agregada correctamente');
    }
    // Solo cerrar el modal de creación, NO el modal de edición
    this.closeCreateModal();
  }

  onPersonSelected(person: any) {
    console.log('Person selected from search:', person);
    console.log('Current selectedPersons:', this.selectedPersons);
    
    if (!person) {
      console.log('No person provided');
      this.closeSearchModal();
      return;
    }
    
    const isAlreadySelected = this.isPersonAlreadySelected(person);
    console.log('Is person already selected?', isAlreadySelected);
    
    if (!isAlreadySelected) {
      this.selectedPersons = [...this.selectedPersons, person];
      this.toastService.showToast('success', 'Persona agregada a la selección');
      console.log('Person added successfully');
    } else {
      this.toastService.showToast('warning', 'Esta persona ya está seleccionada');
      console.log('Person already selected, showing warning');
    }
    
    this.closeSearchModal();
  }


  closeCreateModal() {
    // Solo cerrar el modal de creación específico
    if (this.createModalRef) {
      this.createModalRef.close();
      this.createModalRef = null;
    }
  }

  closeSearchModal() {
    // Solo cerrar el modal de búsqueda específico
    if (this.searchModalRef) {
      this.searchModalRef.close();
      this.searchModalRef = null;
    }
  }

  onCreateCancel() {
    // Solo cerrar el modal de creación cuando se cancela
    this.closeCreateModal();
  }
}